import { motion } from "framer-motion";
import { ShieldCheck, Database, Share2, Timer, Mail } from "lucide-react";

const SECTIONS = [
  {
    icon: Database,
    title: "What we collect",
    body: "When you submit a quote request or an enquiry (as an advertiser or vendor partner), we collect the details you type into the form: your name, email address, business or brand name, city, campaign preferences (cup type, size, quantity, budget or daily volume) and your message. We do not collect payment details, and we do not run advertising trackers on this site.",
  },
  {
    icon: ShieldCheck,
    title: "How we use it",
    body: "Enquiry data is used for one purpose only: to respond to you and to coordinate pilot campaigns — preparing quotes, planning cup placements with vendor partners, and arranging deliveries. Vendor contact details are never shown to advertisers, and advertiser campaign details are never shared with other brands.",
  },
  {
    icon: Share2,
    title: "What we never do",
    body: "We never sell, rent or trade your personal details. We never share your enquiry with third parties except where it is essential to fulfil your order (for example, a delivery courier receiving a delivery address). We do not send marketing email unless you explicitly join our newsletter, and every newsletter carries an unsubscribe option.",
  },
  {
    icon: Timer,
    title: "Storage & retention",
    body: "Enquiries are stored in a secured database hosted with our platform provider. We keep enquiry records for up to 24 months so we can reference ongoing pilot conversations, after which they are deleted. Quote and order records may be kept longer where required for accounting or legal reasons.",
  },
  {
    icon: Mail,
    title: "Your rights",
    body: "You can ask us at any time to show you the data we hold about you, correct it, or delete it entirely. Email enquiry@cupconnect.net with the subject 'Privacy request' and we will action it within 7 working days. If this policy changes, the updated version will be posted on this page with a new effective date.",
  },
];

export const PrivacyPage = () => (
  <main className="pt-32 pb-24" data-testid="privacy-page">
    <div className="max-w-3xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#0D9488] mb-4" data-testid="privacy-eyebrow">
          Privacy Policy
        </p>
        <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl text-[#2E1065]" data-testid="privacy-headline">
          Your enquiry stays{" "}
          <span className="italic text-[#FF5A36]">your business.</span>
        </h1>
        <p className="mt-5 text-[#2E1065]/70 leading-relaxed" data-testid="privacy-intro">
          Effective date: September 2026. Cup Connect is a cup-based advertising
          service — we manufacture branded paper cups and place them with our
          vendor network. This policy explains, in plain language, exactly how
          the details you share through our forms are handled.
        </p>
      </motion.div>

      <div className="mt-12 space-y-5" data-testid="privacy-sections">
        {SECTIONS.map((s, i) => (
          <motion.section
            key={s.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white rounded-3xl p-7 sm:p-8 border border-[#2E1065]/10"
            data-testid={`privacy-section-${i}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-10 h-10 rounded-2xl bg-[#2E1065] flex items-center justify-center shrink-0">
                <s.icon className="w-5 h-5 text-[#FBBF24]" />
              </span>
              <h2 className="font-display font-bold text-xl text-[#2E1065]">{s.title}</h2>
            </div>
            <p className="text-sm sm:text-base text-[#2E1065]/70 leading-relaxed">{s.body}</p>
          </motion.section>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mt-10 text-sm text-[#2E1065]/60 leading-relaxed bg-[#F3EFEA] rounded-2xl p-6"
        data-testid="privacy-contact-note"
      >
        Questions about this policy or your data? Write to{" "}
        <span className="font-bold text-[#2E1065]">enquiry@cupconnect.net</span> — a
        human on our team reads and answers every privacy email.
      </motion.p>
    </div>
  </main>
);
